'use client';
import { hideModal, showToast } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Box, Button, CircularProgress, Stack, TextField } from '@mui/material';
import {
  backofficeErrorMessages,
  postProspectSubstate,
  prospectSubstates,
  substatesFormatted,
} from '@repo/backoffice-api';
import { Doodles } from '@repo/ui';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Renders a ConfirmProspectState body for a Modal component.
 *
 * @return {React.JSX.Element} The rendered ConfirmProspectStateBody component.
 */
export const ChangeProspectSubstateBody = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { prospectId, patchStatus, legalName } = useSelector((state: RootState) => state.prospectData);
  const [loading, setLoading] = useState(false);
  const [headquartersCode, setHeadquartersCode] = useState('');
  const isSelectedSubstateCompleted = patchStatus === 'COMPLETED';

  const getErrorMessage = (errorCode: string): string => {
    const errorMsg = backofficeErrorMessages[errorCode];
    if (!errorCode || !errorMsg) return 'Hubo un error al modificar el estado del prospecto';
    if (errorCode === 'HEADQUARTERS_COMBINATION_MISMATCH') {
      return `${errorMsg} ${legalName}`;
    }
    return errorMsg;
  };

  const handlePatchProspectSubstate = async () => {
    if (prospectId && patchStatus) {
      setLoading(true);
      let notAuth = false;
      try {
        const result = await postProspectSubstate({
          id: prospectId,
          substate: patchStatus as prospectSubstates,
          headquartersCode: isSelectedSubstateCompleted ? headquartersCode : undefined,
        });
        if (result.notAuth) {
          notAuth = true;
          dispatch(showToast({ text: 'Usuario no autenticado', kind: 'error' }));
          throw new Error('User not authenticated');
        }
        if (result.softError || result.hardError) {
          dispatch(showToast({ text: getErrorMessage(result.message), kind: 'error' }));
          throw new Error('Error patching prospect substate: ' + result.message);
        }

        dispatch(
          showToast({
            text: isSelectedSubstateCompleted
              ? `Prospecto ${legalName} completado exitosamente`
              : `Subestado cambiado exitosamente a ${substatesFormatted[patchStatus as prospectSubstates]}`,
            kind: 'success',
          }),
        );
        router.refresh();
        dispatch(hideModal());
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        if (notAuth) {
          dispatch(hideModal());
          router.push('/login');
          router.refresh();
        }
      }
    } else {
      console.error('Invalid data:', prospectId, patchStatus);
      dispatch(showToast({ text: 'Los datos del prospecto a modificar no son válidos', kind: 'error' }));
    }
  };

  return (
    <>
      {isSelectedSubstateCompleted ? (
        <TextField
          label="Código de sucursal"
          placeholder="Ejemplo: 1234"
          value={headquartersCode}
          onChange={(event) => setHeadquartersCode((event.target as HTMLInputElement)?.value.replaceAll(/\D/g, ''))}
        />
      ) : null}
      <Doodles type="confirmation" />
      <Stack direction="row" gap={2} width="100%" mt={2}>
        <Button fullWidth variant="outlined" onClick={() => dispatch(hideModal())}>
          Cancelar
        </Button>
        <Button
          fullWidth
          disabled={!patchStatus || (isSelectedSubstateCompleted && !headquartersCode) || loading}
          onClick={(e) => {
            e.preventDefault();
            handlePatchProspectSubstate();
          }}
        >
          {loading ? (
            <Box sx={{ width: '100%', alignItems: 'center' }}>
              <CircularProgress color="inherit" size={20} />
            </Box>
          ) : (
            <span>Confirmar</span>
          )}
        </Button>
      </Stack>
    </>
  );
};
